import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { MdDelete } from "react-icons/md";
import { IoChatbubbleEllipsesOutline } from "react-icons/io5";
import Select from "react-select";
import LeadDeleteModal from "./LeadDeleteModal";
import { useSelector, useDispatch } from "react-redux";
import { fetchCampaignsAction, fetchLeadsAction } from "../../store/lead/leadThunk";
import { changeWorkerAction } from "../../store/worker/workerThunk";
import { toast } from "react-toastify";
import { ImSpinner8 } from "react-icons/im"; // Import the spinner icon
import { useNavigate } from "react-router-dom";

const LeadTable = ({
  leads = [], // Default value for leads
  onDelete,
  assignedWorkers,
  workers,
  currentPage,
  recordsPerPage,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const { campaigns, fetchLeadsLoading, selectedCampaign } = useSelector((state) => state?.leads);
  const navigate = useNavigate();

  // Filter out only active workers
  const activeWorkers = workers.filter((worker) => worker.Status === "Active");

  const dispatch = useDispatch();
  
  useEffect(() => {
    dispatch(fetchCampaignsAction());
  }, [dispatch]);

  // Continuous polling for leads every 10 seconds
  useEffect(() => {
    let interval;
    
    if (selectedCampaign && selectedCampaign.id) {
      // Set up interval to fetch leads every 10 seconds
      interval = setInterval(() => {
        console.log("Polling leads data...");
        dispatch(
          fetchLeadsAction({
            campaignId: selectedCampaign.id,
            page: currentPage,
            onError: (error) => {
              console.error("Error polling leads:", error);
            },
          })
        );
      }, 10000); // 10 seconds
    }

    // Cleanup interval on component unmount or when dependencies change
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [selectedCampaign, currentPage, dispatch]);

  const openModal = (index) => {
    setRecordToDelete(index);
    setIsModalOpen(true);
  };

  const handleDelete = () => {
    onDelete(recordToDelete);
    setIsModalOpen(false);
  };

  const handleWorkerChange = (selectedWorker, lead) => {
    const { LeadAssignmentId } = lead;
    const workerId = selectedWorker.value;

    dispatch(
      changeWorkerAction({
        LeadAssignmentId,
        Id: workerId,
        onSuccess: () => {
          // Update the local leads state directly for instant UI update
          dispatch({
            type: "leads/updateWorker",
            payload: {
              LeadAssignmentId,
              Worker: selectedWorker.label,
              WorkerId: workerId,
            },
          });
          toast.success("Chatbot changed successfully");
        },
        onError: (error) => {
          toast.error(error);
        },
      })
    );
  };

  const handleSendMessage = (lead) => {
    // Navigate to chat page with the specific lead data
    navigate('/admin/chats', { 
      state: { 
        selectedLead: lead,
        autoOpenChat: true 
      } 
    });
  };

  // Function to check if a lead has unread messages
  const hasUnreadMessages = (lead) => {
    if (!lead.conversation || !lead.conversation.history) {
      return false;
    }
    
    // Check if there are any unread user messages
    return lead.conversation.history.some(
      message => message.type === "User" && message.read === false
    );
  };

  return (
    <>
      <LeadDeleteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDelete}
      />
      <div className="flex justify-between w-full">
        <div className="overflow-x-auto custom-scrollbar w-full">
          <table className="w-[734px] md:w-full overflow-x-auto bg-white border border-gray-200">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">#</th>
                <th className="py-2 px-4 border-b text-left">Name</th>
                <th className="py-2 px-4 border-b text-left">Phone Number</th>
                <th className="py-2 px-4 border-b text-left">Chatbot</th>
                <th className="py-2 px-4 border-b text-left">Send Message</th>
              </tr>
            </thead>
            <tbody>
              {fetchLeadsLoading ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-5 text-[#ff0000a3] font-semibold"
                  >
                    <ImSpinner8 className="spinning-icon animate-spin" />{" "}
                    {/* Add spinner icon */}
                  </td>
                </tr>
              ) : leads.length === 0 || campaigns.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-5 text-[#ff0000a3] font-semibold"
                  >
                    No records found.
                  </td>
                </tr>
              ) : (
                leads.map((item, index) => {
                  const defaultWorker = activeWorkers.find(
                    (worker) => worker.Name === item.Worker
                  );

                  // Calculate row number for pagination
                  const rowNumber = (currentPage - 1) * recordsPerPage + index + 1;
                  
                  // Check if this lead has unread messages
                  const hasUnread = hasUnreadMessages(item);

                  return (
                    <tr key={item?.LeadAssignmentId} className={hasUnread ? "bg-red-50" : ""}>
                      <td className="py-2 px-4 border-b">
                        <div className="relative flex items-center justify-center">
                          <span className="rounded-full mx-auto bg-purple-600 text-white flex items-center justify-center w-8 h-8">
                            {rowNumber}
                          </span>
                          {hasUnread && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse border-2 border-white"></div>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-4 border-b">
                        <div className="flex items-center gap-2">
                          <span>{item?.Name}</span>
                          {hasUnread && (
                            <div className="flex items-center">
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                              <span className="text-red-500 text-xs font-medium ml-1">New</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-4 border-b">{item?.Phone}</td>
                      <td className="py-2 px-4 border-b">{item?.Worker}</td>
                      <td className="py-2 px-4 border-b">
                        <button
                          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 flex items-center justify-center"
                          onClick={() => handleSendMessage(item)}
                        >
                          <IoChatbubbleEllipsesOutline className="mr-1" size={16} />
                          Send Message
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

LeadTable.propTypes = {
  leads: PropTypes.array,
  onDelete: PropTypes.func.isRequired,
  assignedWorkers: PropTypes.array.isRequired,
  workers: PropTypes.array.isRequired,
  currentPage: PropTypes.number.isRequired,
  recordsPerPage: PropTypes.number.isRequired,
};

export default LeadTable;
